import React, { useState } from 'react';
import { 
  PlusIcon, 
  PencilIcon, 
  TrashIcon,
  MagnifyingGlassIcon
} from '@heroicons/react/24/outline';
import { Client, Company } from '../types';

interface ClientListProps {
  clients: Client[];
  currentCompany?: Company | null;
  onAddClient: (client: Omit<Client, 'id' | 'createdAt' | 'updatedAt'>) => void;
  onEditClient: (client: Client) => void;
  onDeleteClient: (clientId: string) => void;
  forceShowAddForm?: boolean;
  onAddFormHandled?: () => void;
}

export const ClientList: React.FC<ClientListProps> = ({ 
  clients, 
  currentCompany,
  onAddClient,
  onEditClient,
  onDeleteClient,
  forceShowAddForm,
  onAddFormHandled
}) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingClient, setEditingClient] = useState<Client | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    cif: '',
    address: '',
    city: '',
    email: '',
    phone: ''
  });

  const filteredClients = clients.filter(client =>
    client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.cif.toLowerCase().includes(searchTerm.toLowerCase()) ||
    client.city.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (editingClient) {
      // Actualizar cliente existente
      const updatedClient: Client = {
        ...editingClient,
        ...formData,
        updatedAt: new Date()
      };
      onEditClient(updatedClient);
      setEditingClient(null);
    } else {
      // Crear nuevo cliente
      onAddClient({
        ...formData,
        companyId: currentCompany?.id || ''
      });
    }
    
    resetForm();
    setShowAddForm(false);
  };

  const handleEdit = (client: Client) => {
    setEditingClient(client);
          setFormData({
        name: client.name,
        cif: client.cif,
        address: client.address || '',
        city: client.city || '',
        email: client.email || '',
        phone: client.phone || ''
      });
    setShowAddForm(true);
  };

  const handleDelete = (clientId: string) => {
    if (window.confirm('¿Estás seguro de que quieres eliminar este cliente? Esta acción no se puede deshacer.')) {
      onDeleteClient(clientId);
    }
  };

  const resetForm = () => {
    setFormData({
      name: '',
      cif: '',
      address: '',
      city: '',
      email: '',
      phone: ''
    });
  };

  const cancelForm = () => {
    setShowAddForm(false);
    setEditingClient(null);
    resetForm();
  };

  // Abrir el formulario si se solicita desde arriba (barra superior)
  React.useEffect(() => {
    if (forceShowAddForm) {
      setEditingClient(null);
      resetForm();
      setShowAddForm(true);
      onAddFormHandled && onAddFormHandled();
    }
  }, [forceShowAddForm, onAddFormHandled]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center space-y-4 sm:space-y-0">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">Clientes</h2>
          <p className="text-gray-600">{filteredClients.length} de {clients.length} clientes</p>
        </div>
        
        <button
          onClick={() => setShowAddForm(true)}
          className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
        >
          <PlusIcon className="h-4 w-4 mr-2" />
          Nuevo Cliente
        </button>
      </div>

      {/* Búsqueda */}
      <div className="relative">
        <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
          <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
        </div>
        <input
          type="text"
          placeholder="Buscar clientes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-primary-500 focus:border-primary-500"
        />
      </div>

      {/* Formulario de cliente */}
      {showAddForm && (
        <div className="bg-white shadow rounded-lg p-6 border">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            {editingClient ? 'Editar Cliente' : 'Nuevo Cliente'}
          </h3>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nombre *
                </label>
                <input
                  type="text"
                  required
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Nombre de la empresa"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  CIF/NIF *
                </label>
                <input
                  type="text"
                  required
                  value={formData.cif}
                  onChange={(e) => setFormData({...formData, cif: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="12345678A"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Dirección
                </label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({...formData, address: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Calle y número"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Ciudad
                </label>
                <input
                  type="text"
                  value={formData.city}
                  onChange={(e) => setFormData({...formData, city: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="Madrid"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Email *
                </label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="cliente@empresa.com"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Teléfono *
                </label>
                <input
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-primary-500 focus:border-primary-500"
                  placeholder="+34 600 000 000"
                />
              </div>
            </div>
            
            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={cancelForm}
                className="px-4 py-2 border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                className="px-4 py-2 bg-primary-600 text-white rounded-md hover:bg-primary-700"
              >
                {editingClient ? 'Actualizar Cliente' : 'Crear Cliente'}
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Lista de clientes */}
      {filteredClients.length === 0 ? (
        <div className="text-center py-12">
          <div className="text-gray-400 text-6xl mb-4">👥</div>
          <h3 className="text-lg font-medium text-gray-900 mb-2">
            {searchTerm ? 'No se encontraron clientes' : 'No hay clientes'}
          </h3>
          <p className="text-gray-500 mb-6">
            {searchTerm 
              ? 'Intenta con otros términos de búsqueda.'
              : 'Comienza creando tu primer cliente.'
            }
          </p>
          {!searchTerm && (
            <button
              onClick={() => setShowAddForm(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-primary-600 hover:bg-primary-700"
            >
              <PlusIcon className="h-4 w-4 mr-2" />
              Crear Primer Cliente
            </button>
          )}
        </div>
      ) : (
        <div className="bg-white shadow overflow-hidden sm:rounded-md">
          <ul className="divide-y divide-gray-200">
            {filteredClients.map((client) => (
              <li key={client.id} className="px-6 py-4 hover:bg-gray-50">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-3">
                      <h3 className="text-lg font-medium text-gray-900 truncate">
                        {client.name}
                      </h3>
                      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {client.cif}
                      </span>
                    </div>
                    
                    <div className="mt-1 flex items-center space-x-4 text-sm text-gray-500">
                      {client.address && (
                        <span className="flex items-center">
                          📍 {client.address}
                        </span>
                      )}
                      {client.city && (
                        <span className="flex items-center">
                          🏙️ {client.city}
                        </span>
                      )}
                      {client.email && (
                        <span className="flex items-center">
                          ✉️ {client.email}
                        </span>
                      )}
                      {client.phone && (
                        <span className="flex items-center">
                          📞 {client.phone}
                        </span>
                      )}
                    </div>
                    
                    <div className="mt-1 text-xs text-gray-400">
                      Creado: {new Date(client.createdAt).toLocaleDateString('es-ES')}
                      {client.updatedAt !== client.createdAt && (
                        <span className="ml-2">
                          | Actualizado: {new Date(client.updatedAt).toLocaleDateString('es-ES')}
                        </span>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => handleEdit(client)}
                      className="p-2 text-blue-400 hover:text-blue-600 hover:bg-blue-50 rounded-md"
                      title="Editar cliente"
                    >
                      <PencilIcon className="h-4 w-4" />
                    </button>
                    
                    <button
                      onClick={() => handleDelete(client.id)}
                      className="p-2 text-red-400 hover:text-red-600 hover:bg-red-50 rounded-md"
                      title="Eliminar cliente"
                    >
                      <TrashIcon className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Resumen */}
      <div className="bg-gray-50 rounded-lg p-4">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
          <div>
            <p className="text-sm font-medium text-gray-600">Total Clientes</p>
            <p className="text-2xl font-bold text-gray-900">{clients.length}</p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Con Email</p>
            <p className="text-2xl font-bold text-blue-600">
              {clients.filter(c => c.email).length}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Con Teléfono</p>
            <p className="text-2xl font-bold text-green-600">
              {clients.filter(c => c.phone).length}
            </p>
          </div>
          <div>
            <p className="text-sm font-medium text-gray-600">Con Dirección</p>
            <p className="text-2xl font-bold text-purple-600">
              {clients.filter(c => c.address && c.city).length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}; 